import React, { useEffect, useState } from 'react';
import type { InventoryItem } from '../warehouseInventoryTypes';
import { getInventoryView, InventoryLotView } from '../../../../services/inventoryApi';
import styles from './InventoryDetailsModal.module.css';

interface InventoryDetailsModalProps {
  item: InventoryItem | null;
  onClose: () => void;
}

export const InventoryDetailsModal: React.FC<InventoryDetailsModalProps> = ({ item, onClose }) => {
  const [lots, setLots] = useState<InventoryLotView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getInventoryView(Number(item.id))
      .then((rows) => { if (!cancelled) setLots(rows); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load lots'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [item]);

  if (!item) return null;

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>{item.sku}</h2>
            <p className={styles.subtitle}>
              {item.category}{item.barcode ? ` · ${item.barcode}` : ''}
            </p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className={styles.stats}>
          <div className={styles.stat}><span>On hand</span><strong>{item.totalQuantity}</strong></div>
          <div className={styles.stat}><span>Available</span><strong>{item.available}</strong></div>
          <div className={styles.stat}><span>Reserved</span><strong>{item.reserved}</strong></div>
          <div className={styles.stat}><span>Locations</span><strong>{item.location}</strong></div>
        </div>

        <h3 className={styles.sectionTitle}>Lots</h3>
        {loading ? (
          <p className={styles.muted}>Loading lots…</p>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : lots.length === 0 ? (
          <p className={styles.muted}>No lots on hand. Receive stock against a purchase order to add inventory.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Lot #</th>
                <th className={styles.num}>Qty</th>
                <th>Location</th>
                <th>Received</th>
                <th>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {lots.map((l) => (
                <tr key={l.id}>
                  <td>{l.lot_number}</td>
                  <td className={styles.num}>{l.quantity_on_hand}</td>
                  <td>{l.location_code}</td>
                  <td>{l.received_date ? new Date(l.received_date).toLocaleDateString() : '—'}</td>
                  <td>{l.expiration_date ? new Date(l.expiration_date).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
